import React, { useState } from "react";
import {
  useListBankChangeRequests,
  useUpdateBankChangeRequest,
  useGetMe,
  getListBankChangeRequestsQueryKey,
  type BankChangeRequest,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, Check, X } from "lucide-react";
import { BankChangeFlowDialog } from "@/components/BankChangeFlowDialog";

const PENDING_STATUSES = ["pending", "action_required", "verified"];

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  cheque: "Cheque / Current",
  savings: "Savings",
};

export function BankChangesList() {
  const { data: me } = useGetMe();
  const { data: requests, isLoading, error } = useListBankChangeRequests();
  const [requestOpen, setRequestOpen] = useState(false);
  const [reviewing, setReviewing] = useState<BankChangeRequest | null>(null);

  const isAgent = me?.role === "agent";
  const canReview = me?.role === "finance" || me?.role === "admin";

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !requests)
    return (
      <div className="p-6 text-red-500">
        Failed to load bank change requests.
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">
          Bank Change Requests
        </h1>
        {isAgent && (
          <Button
            onClick={() => setRequestOpen(true)}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Request bank change
          </Button>
        )}
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            {canReview ? "Verification queue" : "Your requests"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>New Bank</TableHead>
                <TableHead>Account Masked</TableHead>
                <TableHead>Status</TableHead>
                {!canReview && <TableHead>Reviewer notes</TableHead>}
                {canReview && (
                  <TableHead className="text-right">Action</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell className="font-medium">{req.agentName}</TableCell>
                  <TableCell>{req.newBankName}</TableCell>
                  <TableCell>{req.newAccountNumberMasked}</TableCell>
                  <TableCell>
                    <StatusBadge status={req.status} />
                  </TableCell>
                  {!canReview && (
                    <TableCell className="max-w-[280px] whitespace-normal text-sm">
                      {(req.status === "rejected" ||
                        req.status === "action_required") &&
                      req.reviewerNotes ? (
                        <span
                          className={
                            req.status === "rejected"
                              ? "text-red-700"
                              : "text-amber-700"
                          }
                        >
                          {req.reviewerNotes}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                  )}
                  {canReview && (
                    <TableCell className="text-right">
                      {PENDING_STATUSES.includes(req.status) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setReviewing(req)}
                        >
                          Review
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">Closed</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {requests.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center p-8 text-gray-500"
                  >
                    No bank change requests found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isAgent && (
        <BankChangeFlowDialog
          open={requestOpen}
          onOpenChange={setRequestOpen}
        />
      )}
      {canReview && (
        <ReviewDialog request={reviewing} onClose={() => setReviewing(null)} />
      )}
    </div>
  );
}

function ReviewDialog({
  request,
  onClose,
}: {
  request: BankChangeRequest | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const update = useUpdateBankChangeRequest();

  const decide = async (status: "approved" | "rejected") => {
    if (!request) return;
    try {
      await update.mutateAsync({
        id: request.id,
        data: { status, reviewerNotes: notes.trim() || undefined },
      });
      queryClient.invalidateQueries({
        queryKey: getListBankChangeRequestsQueryKey(),
      });
      toast({
        title: status === "approved" ? "Request approved" : "Request rejected",
        description: `${request.agentName}'s bank change was ${status}.`,
      });
      setNotes("");
      onClose();
    } catch {
      toast({
        title: "Could not update request",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">
            Review bank change
          </DialogTitle>
        </DialogHeader>
        {request && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Current bank"
                value={request.currentBankName || "—"}
              />
              <Field label="New bank" value={request.newBankName} />
              <Field
                label="New account"
                value={request.newAccountNumberMasked}
              />
              <Field
                label="Account type"
                value={
                  request.accountType
                    ? (ACCOUNT_TYPE_LABELS[request.accountType] ??
                      request.accountType)
                    : "—"
                }
              />
              <Field label="Branch code" value={request.newBranchCode || "—"} />
            </div>
            <div className="space-y-1.5">
              <Label>Reviewer notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a note about this decision"
                rows={3}
              />
            </div>
          </div>
        )}
        <DialogFooter className="flex-row gap-3">
          <Button
            onClick={() => decide("rejected")}
            disabled={update.isPending}
            variant="outline"
            className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
          >
            <X className="w-4 h-4 mr-2" /> Reject
          </Button>
          <Button
            onClick={() => decide("approved")}
            disabled={update.isPending}
            className="flex-1 bg-[#00A859] hover:bg-[#008f4c] text-white"
          >
            {update.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" /> Approve
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-[#0B132B] break-all">{value}</p>
    </div>
  );
}
